import classNames from "classnames";
import { Divider } from "components/design/Divider";
import { FileIcon } from "components/design/FileIcon";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { ArrowRight } from "components/icons/ArrowRight";
import { DownloadIcon } from "components/icons/Download";
import { Redirect } from "components/icons/Redirect";
import { Preview } from "components/PreviewModal";
import * as FILE_TYPE from "constants/fileTypes";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useWindowSize } from "hooks/useWindowSize";
import { useEffect, useRef, useState } from "react";
import {
  RecentFinancialDocument,
  useGetRecentFinancialsQuery,
} from "store/apis/books";
import { formatDate } from "utils/formatDate";
import { stopPropagation } from "utils/stopPropagation";

const FinancialDocumentCard = ({
  document,
}: {
  document: RecentFinancialDocument;
}) => {
  const {
    downloadFile,
    result: { isFetching: isDownloading },
  } = useDownloadFile();
  const { link } = useConstructInternalLink();
  const { isCustomer } = useRoleBasedView();
  const previewModal = useModal();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isMobile } = useWindowSize();
  const folderLinkTarget = isMobile ? undefined : "_blank";

  const fileType = document.file_type;
  const isPdf = fileType === FILE_TYPE.PDF;
  const isExcel = fileType === FILE_TYPE.EXCEL;

  const folderUrl = isCustomer
    ? `/books/documents/${document.parent_uuid}`
    : `/admin/crm/${document.group_id}/documents/${document.parent_uuid}`;

  const onDownload = () => {
    downloadFile({
      file_id: document.uuid,
      group_id: document.group_id,
    });
  };

  const onPreview = () => {
    previewModal.open();
  };

  return (
    <>
      <div className="t-min-w-[345px] sm:t-min-w-full">
        <button
          onClick={onPreview}
          className="all:unset t-flex t-w-full t-cursor-pointer t-items-center t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-px-4 t-py-3  hover:t-bg-surface-lighter-grey"
        >
          <div className="t-flex t-items-center t-gap-3 !t-w-full">
            <div
              className={classNames(
                "t-shrink-0 t-rounded-full t-p-2 t-flex t-items-center t-justify-center",
                {
                  "t-bg-red-0": isPdf,
                  "t-bg-green-10": isExcel,
                  "t-bg-blue-10": !isPdf && !isExcel,
                }
              )}
            >
              <FileIcon fileType={document.file_type} size="24" />
            </div>
            <div className="t-flex t-flex-col t-gap-1 t-flex-1 t-min-w-0 !t-w-full">
              <div className="t-text-caption-italic t-text-text-60 t-italic t-text-start">
                {document.parent_name} {">"}
              </div>
              <ToolTip text={document.name}>
                <div className="t-text-subtext t-text-text-100 t-truncate t-max-w-[200px]">
                  {document.name}
                </div>
              </ToolTip>
              <div className="t-flex t-flex-row t-gap-2 t-text-body-sm-lite t-text-text-30">
                {document.owner && (
                  <>
                    <div>{document.owner}</div>
                    <Divider />
                  </>
                )}
                <div>{formatDate(document.created_at)}</div>
              </div>
            </div>
            <div className="t-flex t-gap-0.5">
              <Link to={link(folderUrl)} target={folderLinkTarget}>
                <Button
                  size="xsmall"
                  customType="ghost"
                  onClick={stopPropagation}
                >
                  <Redirect size={15} strokeWidth="1" />
                </Button>
              </Link>

              <Button
                size="xsmall"
                customType="ghost"
                isLoading={isDownloading}
                disabled={isDownloading}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                }}
              >
                <DownloadIcon
                  color="currentColor"
                  size={16}
                  strokeWidth="1.2"
                />
              </Button>
            </div>
          </div>
        </button>
      </div>
      {previewModal.isOpen && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={document.uuid}
        />
      )}
    </>
  );
};

export const RecentFinancials = () => {
  const entityId = useCurrentEntityId();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const { data, isLoading, isSuccess } = useGetRecentFinancialsQuery(
    { entityId: entityId! },
    { skip: !entityId }
  );

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    setShowLeftScroll(!isAtStart && scrollWidth > clientWidth);
    setShowRightScroll(!isAtEnd && scrollWidth > clientWidth);
  };

  const scrollByItems = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = 345 + 16;
    const scrollAmount = cardWidth * 2;

    container.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();
    container.addEventListener("scroll", checkScrollPosition);
    window.addEventListener("resize", checkScrollPosition);

    return () => {
      container.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [data]);

  const documents = data || [];
  const isEmpty = documents.length === 0;

  if (isEmpty) return null;

  return (
    <>
      <div className="t-flex t-flex-col t-gap-4 t-overflow-hidden">
        <div className="t-flex t-items-center t-justify-between">
          <div className="t-text-subtitle-lg t-text-text-60 t-m-0">
            Recently Added Financials
          </div>
        </div>

        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={isEmpty}
          renderOnLoad={
            <div className="t-flex t-justify-between t-overflow-x-auto t-gap-4">
              <Skeleton className="t-w-[21rem] t-h-[5.5rem]" />
              <Skeleton className="t-w-[21rem] t-h-[5.5rem]" />
              <Skeleton className="t-w-[21rem] t-h-[5.5rem]" />
            </div>
          }
        >
          <Async.Success>
            <div className="t-relative t-overflow-hidden">
              {showLeftScroll && (
                <div className="t-absolute t-left-0 t-top-0 t-bottom-0 t-z-10 t-pointer-events-none">
                  <div
                    className="t-h-full t-w-40"
                    style={{
                      background:
                        "linear-gradient(to right, #ffffff 25%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                  <button
                    onClick={() => scrollByItems("left")}
                    className="t-absolute t-left-2 t-top-1/2 -t-translate-y-1/2 t-pointer-events-auto  t-rounded-full t-p-0  hover:t-bg-surface-grey t-transition-colors t-border-0 t-bg-i-surface-grey t-rotate-180"
                    aria-label="Scroll left"
                  >
                    <ArrowRight />
                  </button>
                </div>
              )}

              <div
                ref={scrollContainerRef}
                className="t-flex t-gap-4 t-overflow-x-auto t-scrollbar-hide"
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {documents.map((document) => (
                  <FinancialDocumentCard
                    key={document.uuid}
                    document={document}
                  />
                ))}
              </div>

              {showRightScroll && (
                <div className="t-absolute t-right-0 t-top-0 t-bottom-0 t-z-10 t-pointer-events-none">
                  <div
                    className="t-h-full t-w-40"
                    style={{
                      background:
                        "linear-gradient(to left, #ffffff 25%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                  <button
                    onClick={() => scrollByItems("right")}
                    className="t-absolute t-right-2 t-top-1/2 -t-translate-y-1/2 t-pointer-events-auto  t-rounded-full t-p-0  hover:t-bg-surface-grey t-transition-colors t-border-0 t-bg-i-surface-grey"
                    aria-label="Scroll right"
                  >
                    <ArrowRight />
                  </button>
                </div>
              )}
            </div>
          </Async.Success>
        </Async.Root>
      </div>
    </>
  );
};
